// ProductTableBlock — the composable element-list table. It is the React mirror
// of the PDF renderer's ComposeProductTable: it groups the placed instances into
// rows (reusing groupInstancesToRows so the rows match the cart / PDF verbatim),
// resolves each row's display SKU the same way CartModal does, then applies the
// operator's sort + group config using the SAME field vocabulary the PDF uses
// (RowContext.fieldKey ⇆ PdfSummaryRow.FieldKey).
//
// Each column carries its own list of block instances; a cell renders those
// blocks wrapped in a <RowContext.Provider> bound to the row, so the product-*
// blocks read the row out of context. To avoid an import cycle with Slot.tsx
// (which dispatches block instances and would otherwise import this file), the
// actual block→component rendering is injected as the `renderBlocks` callback —
// the caller hands us Slot's renderer.

import { useEffect, useMemo, useState } from "react";
import { fetchProductIds } from "../api/catalogClient";
import { groupInstancesToRows } from "../api/pdfRender";
import type { PriceBreakdownDto } from "../api/priceClient";
import {
  resolveContentSource,
  type ContentSource,
  type ContentSourceContext,
} from "./contentSource";
import { RowContext, fieldKey } from "./RowContext";
import type { ProductRow } from "./RowContext";
import type { BlockHostContext, BlockInstance, BlockUniversalProps } from "./types";
import { resolveRowMaterials } from "../materialResolve";

/** One product-table column: an optional header, sizing/alignment, and the
 *  ordered block instances rendered per row in that column. */
export interface ColumnDef {
  header?: string;
  /** Content source for the header text (literal / dvar / from-*). */
  headerSource?: ContentSource;
  align?: "left" | "center" | "right";
  /** Fixed column width in px. Absent → the column shares remaining space (1fr). */
  widthPx?: number;
  blocks: BlockInstance[];
}

/** A single sort rule. Multiple rules sort lexicographically — the first that
 *  differs wins, each subsequent rule breaks ties (stable, like the C# ThenBy
 *  chain). */
export interface SortRule {
  by: string;
  dir?: "asc" | "desc";
}

/** Grouping config — partitions the (already sorted) rows by a field, each group
 *  optionally preceded by a full-width header row. */
export interface GroupConfig {
  by: string;
  dir?: "asc" | "desc";
  showHeader?: boolean;
  headerLabelPrefix?: string;
  /** Operator-authored header blocks, bound to the group's first row so they can
   *  read its product fields. Absent → a default uppercased label cell. */
  headerBlocks?: BlockInstance[];
}

export interface ProductTableBlockProps extends BlockUniversalProps {
  host: BlockHostContext;
  columns: ColumnDef[];
  sort?: SortRule[];
  group?: GroupConfig;
  /** Injected block renderer (Slot's renderBlock list-walker). Passed in rather
   *  than imported so this file never depends on Slot.tsx — that would be a
   *  cycle (Slot dispatches into this component). */
  renderBlocks: (blocks: BlockInstance[], host: BlockHostContext) => React.ReactNode;
  /** Live content-source context (series + selection), handed down by Slot.
   *  Used to resolve column headers that draw from a live source. */
  sourceCtx?: ContentSourceContext;
}

/** A resolved group: the partition key, a display label for the default header,
 *  and the rows in it (in their incoming, already-sorted order). */
interface RowGroup {
  key: string;
  label: string;
  rows: ProductRow[];
}

export function ProductTableBlock({
  host,
  columns,
  sort,
  group,
  renderBlocks,
  themeVariant,
  className,
  sourceCtx,
}: ProductTableBlockProps) {
  // Build the base rows from the placed instances. groupInstancesToRows reuses
  // the cart/PDF grouping exactly (productId + material picks + variant + group
  // membership → one row, qty N), then name-sorts so "no sort" stays A→Z.
  const baseRows: ProductRow[] = useMemo(() => {
    return groupInstancesToRows(host.instances, host.productById).map(r => {
      const params = r.product?.parameterValues ?? {};
      return {
        // productId starts "" and is filled in by the SKU-resolution pass below.
        productId: "",
        name: r.product?.name ?? "",
        imageUrl: r.product?.imageUrl ?? undefined,
        quantity: r.qty,
        parameters: params,
        category: params["category"] ?? params["Category"],
        // Series falls back to the active Designer series label, matching how
        // buildSceneSummaryContext prefers the live series over a parameter.
        series: host.activeSeries?.label ?? params["series"] ?? params["Series"],
        groupId: r.groupId,
        groupName: r.groupName,
        // Resolve the user's picks (surfaceKey → materialId) to display names
        // via the product detail cache. Empty until the detail warms (the cart
        // warm-pass fetches it); the block just renders nothing meanwhile.
        materials: resolveRowMaterials(r.productId, r.materialPicks, host.detailCache),
      } satisfies ProductRow;
    });
    // host.instances / productById / activeSeries feed the rows; detailCache is
    // mutated in place (stable identity) so we key on detailCacheVersion to
    // recompute resolved materials when the cart's warm-pass fills the cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host.instances, host.productById, host.activeSeries, host.detailCacheVersion]);

  // SKU lookup per row — the existing endpoint honours material picks + manual
  // overrides so it can't be computed client-side. Mirrors CartModal's useEffect.
  const [productIds, setProductIds] = useState<Map<string, string>>(new Map());
  // Re-key the rows for the lookup by their stable group keys, so the effect
  // only re-fires when the actual row set changes.
  const groupKeys = useMemo(
    () => groupInstancesToRows(host.instances, host.productById).map(r => r.groupKey),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [host.instances, host.productById],
  );
  const rowsSig = useMemo(() => groupKeys.slice().sort().join("|"), [groupKeys]);
  useEffect(() => {
    const grouped = groupInstancesToRows(host.instances, host.productById);
    if (grouped.length === 0) {
      setProductIds(new Map());
      return;
    }
    const ctrl = new AbortController();
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetchProductIds({
          baseUrl: host.baseUrl,
          body: {
            items: grouped.map(r => ({
              groupKey: r.groupKey,
              productId: r.productId,
              materialPicks: r.materialPicks,
            })),
          },
          signal: ctrl.signal,
        });
        if (cancelled) return;
        const next = new Map<string, string>();
        for (const r of resp.results) {
          if (r.productId) next.set(r.groupKey, r.productId);
        }
        setProductIds(next);
      } catch {
        if (!cancelled) setProductIds(new Map());
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsSig, host.baseUrl]);

  // Merge resolved SKUs into the rows — plus each row's price from the host's
  // live breakdown (matched by the same resolved SKU the price request used).
  // groupInstancesToRows is deterministic, so baseRows and groupKeys line up
  // index-for-index.
  const breakdownFeature = host.getFeature("priceBreakdown");
  const breakdown =
    breakdownFeature && typeof breakdownFeature === "object"
      ? (breakdownFeature as PriceBreakdownDto)
      : null;
  const rows: ProductRow[] = useMemo(() => {
    return baseRows.map((row, i) => {
      const sku = productIds.get(groupKeys[i]) ?? "";
      const line = sku && breakdown ? breakdown.lines.find(l => l.productId === sku) : undefined;
      return {
        ...row,
        productId: sku,
        unitPrice: line?.priced
          ? line.formattedUnitAmount ?? `${line.unitAmount} ${breakdown!.currency}`
          : undefined,
        lineTotal: line?.priced
          ? line.formattedLineTotal ?? `${line.lineTotal} ${breakdown!.currency}`
          : undefined,
        includedQuantity: line?.includedQuantity || undefined,
      };
    });
  }, [baseRows, groupKeys, productIds, breakdown]);

  // Apply sort + group. Both pure — recompute only when the rows or config move.
  const groups: RowGroup[] = useMemo(
    () => partitionGroups(sortRows(rows, sort), group),
    [rows, sort, group],
  );

  // CSS-grid template from the column widths: fixed px where set, else 1fr.
  const gridTemplate = useMemo(
    () => columns.map(c => (c.widthPx != null ? `${c.widthPx}px` : "1fr")).join(" "),
    [columns],
  );

  const rootClass = "sd-pt" + (className ? " " + className : "");

  // Empty instances → the cart's familiar drop hint.
  if (host.instances.length === 0) {
    return (
      <div className={rootClass} data-variant={themeVariant}>
        <p className="sd-cart__empty">Drag a product from the catalog to add it here.</p>
      </div>
    );
  }

  return (
    <div className={rootClass} data-variant={themeVariant}>
      {/* Header is a fixed grid OUTSIDE the scroll area — its own grid with the
          same column template so it lines up with the body. Keeping it out of
          the scroller means the scrollbar only spans the product rows, and the
          header's hairline rules don't repaint/flicker on scroll. */}
      <div
        className="sd-pt__headgrid"
        style={{ display: "grid", gridTemplateColumns: gridTemplate }}
      >
        {columns.map((c, ci) => (
          <span
            key={"h" + ci}
            className="sd-pt__col sd-cart__col"
            style={{ textAlign: c.align ?? "left" }}
          >
            {resolveContentSource(c.headerSource, c.header, sourceCtx ?? {}) ?? ""}
          </span>
        ))}
      </div>

      {/* Scrolling body — only the product rows scroll. Shares the header's
          column template so cells align with the header above. */}
      <div className="sd-pt__bodyscroll">
        <div
          className="sd-pt__grid"
          style={{ display: "grid", gridTemplateColumns: gridTemplate }}
        >
          {groups.map((grp, gi) => (
            <div key={"g" + gi} className="sd-pt__group" style={{ display: "contents" }}>
              {group != null && (group.showHeader ?? true) && grp.rows.length > 0 && (
                <div
                  className="sd-pt__group-head sd-cart__group-head"
                  style={{ gridColumn: "1 / -1" }}
                >
                  {group.headerBlocks && group.headerBlocks.length > 0 ? (
                    <RowContext.Provider value={grp.rows[0]}>
                      {renderBlocks(group.headerBlocks, host)}
                    </RowContext.Provider>
                  ) : (
                    (group.headerLabelPrefix ?? "") + grp.label
                  )}
                </div>
              )}
              {grp.rows.map((row, ri) => (
                <div key={"r" + gi + "_" + ri} className="sd-pt__row" style={{ display: "contents" }}>
                  {columns.map((c, ci) => (
                    <div
                      key={"c" + ci}
                      className="sd-pt__cell"
                      style={{ textAlign: c.align ?? "left" }}
                    >
                      <RowContext.Provider value={row}>
                        {renderBlocks(c.blocks, host)}
                      </RowContext.Provider>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Stable, multi-key sort over the rows — mirrors the C# SortRows (and its
 *  NaturalComparer). Empty / absent rules leave the rows in arrival order
 *  (already name-sorted by groupInstancesToRows). numeric:true gives natural
 *  alphanumeric order so embedded numbers compare by value — "Royal-2" before
 *  "Royal-10", not after — matching the PDF. Quantity's zero-padded fieldKey
 *  still orders numerically under numeric:true (leading zeros are ignored).
 *  desc reverses the rule. The chain is applied right-to-left so the first rule
 *  is primary and later rules break ties (Array.prototype.sort is stable). */
function sortRows(rows: ProductRow[], sort: SortRule[] | undefined): ProductRow[] {
  if (!sort || sort.length === 0) return rows;
  const out = rows.slice();
  for (let i = sort.length - 1; i >= 0; i--) {
    const rule = sort[i];
    const desc = rule.dir === "desc";
    out.sort((a, b) => {
      const cmp = fieldKey(a, rule.by).localeCompare(fieldKey(b, rule.by), undefined, {
        sensitivity: "accent",
        numeric: true,
      });
      return desc ? -cmp : cmp;
    });
  }
  return out;
}

/** Partition the (already row-sorted) rows by a field — mirrors the C#
 *  PartitionGroups. No config → one implicit unkeyed group preserving row order.
 *  With config, groups appear in key order (asc/desc) while each group keeps its
 *  incoming row order; empty keys ("ungrouped") sort to the end. */
function partitionGroups(rows: ProductRow[], group: GroupConfig | undefined): RowGroup[] {
  if (!group) {
    return [{ key: "", label: "", rows: rows.slice() }];
  }
  const map = new Map<string, RowGroup>();
  const order: string[] = [];
  for (const row of rows) {
    const key = fieldKey(row, group.by);
    let g = map.get(key);
    if (!g) {
      g = { key, label: key === "" ? "Ungrouped" : key, rows: [] };
      map.set(key, g);
      order.push(key);
    }
    g.rows.push(row);
  }
  const desc = group.dir === "desc";
  const orderedKeys = order.slice().sort((a, b) => {
    // Real groups first, "ungrouped" (empty key) last.
    const aEmpty = a === "" ? 1 : 0;
    const bEmpty = b === "" ? 1 : 0;
    if (aEmpty !== bEmpty) return aEmpty - bEmpty;
    // numeric:true → natural order so group keys with embedded numbers
    // (Royal-2, Royal-10) partition the way a human reads them, matching the
    // PDF's PartitionGroups.
    const cmp = a.localeCompare(b, undefined, { sensitivity: "accent", numeric: true });
    return desc ? -cmp : cmp;
  });
  return orderedKeys.map(k => map.get(k) as RowGroup);
}
